/**
 * Helper functions for accessing properties from CRM objects.
 *
 * Properties are stored in the `properties` field with typed metadata.
 * Use getPropertyValue() to extract the raw value from the PropertyValue wrapper.
 *
 * Note: Pipeline stages for deals and lifecycle stages for contacts are now
 * handled via associations, not property values.
 * Use the useStages hook to look up stage data by entity_id.
 */

import type { CrmObject, PropertyValue } from '../../../types/crm';
import { getPropertyValue } from '../../../types/crm';

/**
 * Get stage entity_id from object.
 * - Deals: Read from associations (ahoy_deal_to_deal_pipeline_stage)
 * - Contacts: Read from associations (ahoy_contact_to_lifecycle_stage)
 * - Custom pipelineable entities: Read from any association targeting a stage type
 * The value is a entity_id string referencing a stage CRM object.
 *
 * @param obj - The CRM object to get stage entity ID from
 * @param stageEntityTypes - Optional set of known stage entity type names (from useStages).
 *   When provided, uses set-based lookup instead of suffix matching for generic fallback.
 */
export function getStageEntityId(
  obj: CrmObject,
  stageEntityTypes?: Set<string>
): string | null {
  if (obj.entity_type_name === 'ahoy_deal') {
    // Deals use associations for stage
    const stageAssoc = obj.associations?.find(
      a => a.association_type_name === 'ahoy_deal_to_deal_pipeline_stage'
    );
    return stageAssoc?.target_entity_id ?? null;
  }

  if (obj.entity_type_name === 'ahoy_contact') {
    // Contacts use associations for lifecycle stage
    const stageAssoc = obj.associations?.find(
      a => a.association_type_name === 'ahoy_contact_to_lifecycle_stage'
    );
    return stageAssoc?.target_entity_id ?? null;
  }

  // Generic fallback: find any association targeting a stage type
  // This handles custom pipelineable entities (e.g., org_project_to_org_project_pipeline_stage).
  // Requires stageEntityTypes to be provided for type-safe stage detection.
  if (stageEntityTypes && stageEntityTypes.size > 0) {
    // Association type names follow the pattern "{source}_to_{target}".
    // Match by checking if any association type name ends with "_to_{stageTypeName}"
    // for exact target type matching against the provided set.
    const stageAssoc = obj.associations?.find(a =>
      Array.from(stageEntityTypes).some(sType =>
        a.association_type_name.endsWith(`_to_${sType}`)
      )
    );
    return stageAssoc?.target_entity_id ?? null;
  }

  // No stage types provided - unable to detect stage association
  return null;
}

/**
 * Get stage name (now entity_id) from object.
 * @deprecated Stage is now stored as a entity_id. Use getStageEntityId() instead
 * and look up stage details via useStages hook.
 */
export function getStageName(obj: CrmObject): string | null {
  return getStageEntityId(obj);
}

/**
 * Get created_at timestamp from properties.ahoy_created_at
 */
export function getCreatedAt(obj: CrmObject): string | null {
  const createdAt = getPropertyValue<string>(
    obj.properties?.ahoy_created_at as PropertyValue | undefined
  );
  return typeof createdAt === 'string' ? createdAt : null;
}

/**
 * Get updated_at timestamp from properties.ahoy_updated_at
 */
export function getUpdatedAt(obj: CrmObject): string | null {
  const updatedAt = getPropertyValue<string>(
    obj.properties?.ahoy_updated_at as PropertyValue | undefined
  );
  return typeof updatedAt === 'string' ? updatedAt : null;
}
